import { useEffect } from "react";
import { ApiShipmentType } from "entities/shared/shipmentCard.types";
import { ShipmentModel } from "features/shared/shipmentCard.types";
import { useFilterContext } from "features/Filter";
import { usePanelViews } from "features/shared/usePanelViews.hook";
import { airCardDeleteModalStore } from "entities/AirCardDeleteModal";
import {
  AirSavedCardModel,
  airSavedCardStore,
  isAirSavedCardModel,
  useAirDeleteCardMutation,
  useFollowCardMutation,
  useSavedCardsQuery,
} from "entities/AirSavedCard";
import { formatAirCargo } from "entities/AirSavedCard/api/cargo";
import { AirSearchCardModel, airSearchCardStore, isAirSearchCardModel } from "entities/AirSearchCard";
import { appStore } from "entities/App";
import { authStore } from "entities/Auth";
import { resolveDateValues } from "entities/shared/utils";
import { voidFunc } from "shared/lib/common.lib";
import { useAppDispatch, useAppSelector } from "shared/lib/redux";
import { Nullable } from "shared/lib/typeCheck.lib";

import { getFollowCardPin, mapAirStatusForSaved } from "../utils";

type AirCardModel = AirSavedCardModel | AirSearchCardModel;

type FollowFunc = (card: AirSearchCardModel) => void;
type AskDeletingSavedCardFunc = (card: AirCardModel) => void;
type DeleteCardHandler = (card: AirCardModel) => void;
type CancelDeletingSavedCardFunc = () => void;
type DeleteCardModalHandlerFunc = () => void;
type UseAirCardActionsReturnType = {
  deleteAirCardHandler: DeleteCardHandler;
  deleteAirCardModalHandler: DeleteCardModalHandlerFunc;
  cancelAirCardDeleting: CancelDeletingSavedCardFunc;
  airCardFollow: FollowFunc;
  isAirCardFollowLoading: boolean;
  isAirCardFollowButtonVisible: boolean;
  isAirCardDeleting: boolean;
};

type UseAirCardActionsParams = {
  cardToDelete?: Nullable<ShipmentModel>;
  setCardToDeleteAction?: (card: ShipmentModel) => {
    type: string;
    payload: ShipmentModel | null;
  };
  resetCardToDeleteAction?: () => {
    type: string;
  };
  removeFilteredCardAction: (card: ShipmentModel) => {
    type: string;
    payload: ShipmentModel;
  };
  removeCardByTagAction: (card: ShipmentModel) => {
    type: string;
    payload: ShipmentModel;
  };
  refreshTags: () => void;
};

export const useAirCardActions = ({
  cardToDelete,
  setCardToDeleteAction,
  resetCardToDeleteAction,
  removeFilteredCardAction,
  removeCardByTagAction,
  refreshTags,
}: UseAirCardActionsParams): UseAirCardActionsReturnType => {
  const dispatch = useAppDispatch();
  const isAuthenticated = useAppSelector(authStore.selectors.isAuthenticated);
  //   const { cardToDelete, savedCards, cardsByTag, filteredCards, searchCards } = useAppSelector(
  //     (state) => state.panel
  //   );
  const requestedFromUrl = useAppSelector(appStore.selectors.requestedFromUrl);
  const savedCards = useAppSelector(airSavedCardStore.selectors.savedCards);
  const searchCards = useAppSelector(airSearchCardStore.selectors.searchCards);
  const { showPreviousView, isCurrentViewOpenedCard } = usePanelViews();

  const { getCards, removeCardsCache } = useSavedCardsQuery({ queryKey: ["check-air-existing-shipment"] });
  const { followCard, isCardFollowLoading } = useFollowCardMutation();
  const { deleteCard: deleteSavedCardRequest, isCardDeleting: isSavedCardDeleting } = useAirDeleteCardMutation();
  const { setAllStatuses } = useFilterContext();

  // Asks the server for the current status counts instead of guessing them
  // on the client - after a follow or a delete this is the source of truth.
  const { getCards: getStatusCounts, countByStatus: refreshedStatusCounts } = useSavedCardsQuery({
    queryKey: ["air-saved-status-counts"],
  });

  useEffect(() => {
    if (!refreshedStatusCounts) return;
    setAllStatuses(refreshedStatusCounts);
  }, [refreshedStatusCounts]);

  const refreshStatusCounts = () => {
    getStatusCounts({ limit: 1 });
  };

  const removeSavedCardFromUI = (card: AirSavedCardModel) => {
    dispatch(airSavedCardStore.actions.removeSavedCardAction(card));
    dispatch(removeFilteredCardAction(card));

    dispatch(removeCardByTagAction(card));
    refreshStatusCounts();
  };

  const resetDeleteModal = () => {
    dispatch(airCardDeleteModalStore.actions.closeDeleteModalAction());
    if (resetCardToDeleteAction) {
      dispatch(resetCardToDeleteAction());
    }
  };

  const handleModalDeleting = () => {
    resetDeleteModal();
    if (isAuthenticated) {
      refreshTags();
    }
    if (isCurrentViewOpenedCard) {
      showPreviousView();
    }
  };

  const askDeletingSavedCard: AskDeletingSavedCardFunc = (card) => {
    dispatch(airCardDeleteModalStore.actions.openDeleteCardModalAction());
    if (setCardToDeleteAction) {
      dispatch(setCardToDeleteAction(card));
    }
  };

  const createAirDeleteHandlers = (): {
    deleteAirCardHandler: DeleteCardHandler;
    deleteAirCardModalHandler: DeleteCardModalHandlerFunc;
  } => {
    // case 1: opened from url (and clicked delete button)
    if (isCurrentViewOpenedCard && requestedFromUrl) {
      return {
        deleteAirCardHandler: (card) => {
          askDeletingSavedCard(card);
        },
        deleteAirCardModalHandler: () => {
          if (!cardToDelete) {
            handleModalDeleting();
            return;
          }

          if (!isAuthenticated && isAirSearchCardModel(cardToDelete)) {
            dispatch(airSearchCardStore.actions.removeSearchCardAction(cardToDelete));
            handleModalDeleting();
            return;
          }

          if (isAuthenticated && isAirSearchCardModel(cardToDelete) && cardToDelete.reference) {
            const existingSavedCard = savedCards.find((c) => c.reference === cardToDelete.reference);
            //  ?? cardsByTag.find((c) => c.reference === cardToDelete.reference) ??
            //   filteredCards.find((c) => c.reference === cardToDelete.reference);

            if (existingSavedCard) {
              deleteSavedCardRequest(existingSavedCard.id, {
                onSettled: () => {
                  removeSavedCardFromUI(existingSavedCard);
                  handleModalDeleting();
                },
              });
              return;
            }

            const existingSearchCard = searchCards.find((c) => c.reference === cardToDelete.reference);

            if (existingSearchCard) {
              dispatch(airSearchCardStore.actions.removeSearchCardAction(cardToDelete));
              handleModalDeleting();
            }

            getCards(
              { limit: 1, where: { reference: cardToDelete.reference } },
              {
                onSettled: (response) => {
                  if (response?.length) {
                    deleteSavedCardRequest(response[0].id, {
                      onSettled: () => {
                        removeCardsCache();
                        handleModalDeleting();
                      },
                    });
                  }
                },
              }
            );
            return;
          }

          if (isAirSavedCardModel(cardToDelete)) {
            removeSavedCardFromUI(cardToDelete);
            deleteSavedCardRequest(cardToDelete.id, {
              onSettled: () => handleModalDeleting(),
            });
          }
        },
      };
    }

    // case 2: clicked delete button on openned card or closed card
    if (!requestedFromUrl) {
      return {
        deleteAirCardHandler: (card) => {
          askDeletingSavedCard(card);
        },
        deleteAirCardModalHandler: () => {
          if (isAirSavedCardModel(cardToDelete)) {
            deleteSavedCardRequest(cardToDelete.id, {
              onSettled: () => {
                removeSavedCardFromUI(cardToDelete);
                handleModalDeleting();
              },
            });
            return;
          }
          if (isAirSearchCardModel(cardToDelete)) {
            dispatch(airSearchCardStore.actions.removeSearchCardAction(cardToDelete));
            handleModalDeleting();
            return;
          }
        },
      };
    }

    return {
      deleteAirCardHandler: voidFunc,
      deleteAirCardModalHandler: voidFunc,
    };
  };

  const follow: FollowFunc = (data) => {
    if (!data || !data?.reference) return;

    const { atd, etd, ata, eta } = data;
    const { departureDate, arrivalDate } = resolveDateValues({ atd, etd, ata, eta });

    followCard(
      {
        reference: data.reference,
        shipment_type: ApiShipmentType.AIR,
        type: "AWB",
        location: {
          pin: getFollowCardPin(data),
          from: data.directionFrom ?? null,
          to: data.directionTo ?? null,
          transport: "AIR",
        },
        carrier_scac: data.iataCode || null,
        carrier_name: data.airlineName || null,
        status: mapAirStatusForSaved(data.status),
        equipments_count: data.piece,
        size_type: formatAirCargo({ pieces: data.piece, weight: data.weight }),
        tags: [],
        date: {
          from_actual: departureDate.isActual,
          from: departureDate.value,
          to_actual: arrivalDate.isActual,
          to: arrivalDate.value,
          to_predictive: null,
        },
      },
      {
        onSuccess: (response) => {
          dispatch(airSearchCardStore.actions.removeSearchCardAction(data));
          dispatch(airSavedCardStore.actions.prependSavedCardAction({ data: response }));
          refreshStatusCounts();
        },
      }
    );
  };

  const cancelAirCardDeleting: CancelDeletingSavedCardFunc = () => {
    if (isSavedCardDeleting) return;
    resetDeleteModal();
  };

  return {
    ...createAirDeleteHandlers(),
    cancelAirCardDeleting,
    airCardFollow: follow,
    isAirCardFollowLoading: isCardFollowLoading,
    // See the sea hook: one shared mutation must not decide visibility for
    // every card in the list.
    isAirCardFollowButtonVisible: isAuthenticated,
    isAirCardDeleting: isSavedCardDeleting,
  };
};
